import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Plus, Pencil, Ban, RotateCcw, Users, Bike, Utensils, Search } from "lucide-react";

const EMPTY = { nome: "", cognome: "", telefono: "", partecipanti: 1, moto: 1, pranzo: false, persone_pranzo: 0, note: "" };

function SignupForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const partecipanti = Number(f.partecipanti) || 0;
  const persone = Number(f.persone_pranzo) || 0;
  const err = f.pranzo && persone < 1 ? "Indica quante persone si fermano a pranzo"
    : persone > partecipanti ? "Le persone a pranzo non possono superare i partecipanti"
    : (Number(f.moto) || 0) > partecipanti ? "Le moto non possono superare i partecipanti" : "";

  const submit = async (e) => {
    e.preventDefault();
    if (err) return;
    setSaving(true);
    const body = { ...f, partecipanti, moto: Number(f.moto) || 0, persone_pranzo: f.pranzo ? persone : 0 };
    try {
      if (initial?.id) await api.put(`/moto-signups/${initial.id}`, body);
      else await api.post("/moto-signups/admin", body);
      toast.success("Iscrizione salvata");
      onSaved();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="moto-admin-form">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase tracking-wider text-oak/70">Nome *</Label><Input required value={f.nome} onChange={(e) => set("nome", e.target.value)} data-testid="moto-admin-nome" /></div>
        <div><Label className="text-xs uppercase tracking-wider text-oak/70">Cognome *</Label><Input required value={f.cognome} onChange={(e) => set("cognome", e.target.value)} data-testid="moto-admin-cognome" /></div>
      </div>
      <div><Label className="text-xs uppercase tracking-wider text-oak/70">Telefono *</Label><Input required type="tel" value={f.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="moto-admin-telefono" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase tracking-wider text-oak/70">Partecipanti *</Label><Input required type="number" min="1" max="50" value={f.partecipanti} onChange={(e) => set("partecipanti", e.target.value)} data-testid="moto-admin-partecipanti" /></div>
        <div><Label className="text-xs uppercase tracking-wider text-oak/70">Moto *</Label><Input required type="number" min="0" max="50" value={f.moto} onChange={(e) => set("moto", e.target.value)} data-testid="moto-admin-moto" /></div>
      </div>
      <div className="flex items-center gap-3">
        <Label className="text-xs uppercase tracking-wider text-oak/70">Pranzo</Label>
        <div className="flex gap-2">
          {[[true, "Sì"], [false, "No"]].map(([v, l]) => (
            <button key={l} type="button" onClick={() => set("pranzo", v)} data-testid={`moto-admin-pranzo-${l.toLowerCase()}`}
              className={`px-4 py-1.5 rounded-full text-sm border ${f.pranzo === v ? "bg-bordeaux text-cream border-bordeaux" : "bg-white text-oak border-brass/30"}`}>{l}</button>
          ))}
        </div>
        {f.pranzo && (
          <div className="flex-1"><Input type="number" min="1" max={partecipanti || 50} value={f.persone_pranzo || ""} onChange={(e) => set("persone_pranzo", e.target.value)} placeholder="Persone a pranzo" data-testid="moto-admin-persone-pranzo" /></div>
        )}
      </div>
      <div><Label className="text-xs uppercase tracking-wider text-oak/70">Note</Label><Textarea rows={2} value={f.note || ""} onChange={(e) => set("note", e.target.value)} data-testid="moto-admin-note" /></div>
      {err && <p className="text-sm text-bordeaux" data-testid="moto-admin-error">{err}</p>}
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" onClick={onCancel}>Annulla</Button>
        <Button type="submit" disabled={saving || !!err} className="bg-bordeaux hover:bg-bordeaux-dark text-cream" data-testid="moto-admin-save">{saving ? "Salvataggio…" : "Salva"}</Button>
      </div>
    </form>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-xl border border-brass/15 p-4 flex items-center gap-3" data-testid={testid}>
      <span className="w-10 h-10 rounded-full bg-bordeaux/10 text-bordeaux flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
      <div><p className="text-2xl font-serif text-bordeaux leading-none">{value}</p><p className="text-xs text-oak/60 mt-1">{label}</p></div>
    </div>
  );
}

export default function MotoSignupsView() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("iscritta");
  const [editing, setEditing] = useState(null); // null | {} | row

  const load = () => api.get("/moto-signups").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const active = rows.filter((r) => (r.stato || "iscritta") === "iscritta");
  const totals = useMemo(() => active.reduce((a, r) => ({
    iscrizioni: a.iscrizioni + 1,
    partecipanti: a.partecipanti + (Number(r.partecipanti ?? r.persone) || 0),
    moto: a.moto + (Number(r.moto) || 0),
    pranzo: a.pranzo + (Number(r.persone_pranzo) || 0),
  }), { iscrizioni: 0, partecipanti: 0, moto: 0, pranzo: 0 }), [active]);

  const shown = rows.filter((r) => {
    const st = r.stato || "iscritta";
    if (filter !== "tutte" && st !== filter) return false;
    const s = q.trim().toLowerCase();
    return !s || `${r.nome} ${r.cognome} ${r.telefono || r.whatsapp || ""}`.toLowerCase().includes(s);
  });

  const setStato = async (r, stato) => {
    try { await api.patch(`/moto-signups/${r.id}/stato`, { stato }); toast.success(stato === "annullata" ? "Iscrizione annullata" : "Iscrizione riattivata"); load(); }
    catch (e) { toast.error("Operazione non riuscita"); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/moto-signups/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "motoraduno_masetti.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Esportazione fallita"); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div className="space-y-4" data-testid="moto-admin">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Users} label="Iscrizioni attive" value={totals.iscrizioni} testid="moto-stat-iscrizioni" />
        <Stat icon={Users} label="Partecipanti" value={totals.partecipanti} testid="moto-stat-partecipanti" />
        <Stat icon={Bike} label="Moto" value={totals.moto} testid="moto-stat-moto" />
        <Stat icon={Utensils} label="Persone a pranzo" value={totals.pranzo} testid="moto-stat-pranzo" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-oak/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, cognome o telefono" className="pl-9 bg-white" data-testid="moto-admin-search" />
        </div>
        <div className="flex gap-1 bg-cream-dark rounded-full p-1">
          {[["iscritta", "Attive"], ["annullata", "Annullate"], ["tutte", "Tutte"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`moto-admin-filter-${v}`}
              className={`px-3 py-1 rounded-full text-sm ${filter === v ? "bg-bordeaux text-cream" : "text-oak/70"}`}>{l}</button>
          ))}
        </div>
        <Button onClick={() => setEditing({})} className="bg-bordeaux hover:bg-bordeaux-dark text-cream" data-testid="moto-admin-add"><Plus className="w-4 h-4 mr-1" />Nuova iscrizione</Button>
        <Button onClick={exportCsv} className="bg-brass hover:bg-brass-hover text-oak-dark" data-testid="export-moto-csv"><Download className="w-4 h-4 mr-2" />Esporta CSV</Button>
      </div>

      <div className="space-y-2">
        {shown.map((r) => {
          const annullata = (r.stato || "iscritta") === "annullata";
          return (
            <div key={r.id} className={`bg-white rounded-lg border p-4 ${annullata ? "border-oak/10 opacity-70" : "border-brass/15"}`} data-testid={`moto-signups-item-${r.id}`}>
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div className="min-w-0 text-sm space-y-1">
                  <p className="font-serif text-lg text-bordeaux leading-tight">
                    {r.nome} {r.cognome}
                    <span className={`ml-2 align-middle text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full ${annullata ? "bg-oak/10 text-oak/70" : "bg-brass/20 text-oak-dark"}`} data-testid={`moto-stato-${r.id}`}>{annullata ? "Annullata" : "Iscritta"}</span>
                  </p>
                  <p className="text-oak/80"><a href={`tel:${r.telefono || r.whatsapp}`} className="underline underline-offset-2">{r.telefono || r.whatsapp}</a></p>
                  <p className="text-oak/80">
                    <b>{r.partecipanti ?? r.persone}</b> partecipanti · <b>{r.moto ?? "—"}</b> moto · Pranzo: <b>{r.pranzo === true ? `Sì (${r.persone_pranzo})` : r.pranzo === false ? "No" : r.pranzo || "—"}</b>
                  </p>
                  {r.note && <p className="text-oak/70 italic">{r.note}</p>}
                  <p className="text-xs text-oak/50">Iscrizione: {fmt(r.created_at)}{r.origine === "admin" ? " · inserita manualmente" : ""}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setEditing(r)} className="border-brass/30" data-testid={`moto-edit-${r.id}`}><Pencil className="w-4 h-4 mr-1" />Modifica</Button>
                  {annullata
                    ? <Button size="sm" variant="outline" onClick={() => setStato(r, "iscritta")} className="border-brass/30" data-testid={`moto-restore-${r.id}`}><RotateCcw className="w-4 h-4 mr-1" />Riattiva</Button>
                    : <Button size="sm" variant="outline" onClick={() => { if (window.confirm("Annullare questa iscrizione?")) setStato(r, "annullata"); }} className="border-bordeaux/30 text-bordeaux" data-testid={`moto-cancel-${r.id}`}><Ban className="w-4 h-4 mr-1" />Annulla</Button>}
                </div>
              </div>
            </div>
          );
        })}
        {shown.length === 0 && <p className="text-oak/50 text-sm py-6 text-center" data-testid="moto-admin-empty">Nessuna iscrizione.</p>}
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-cream max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-bordeaux">{editing?.id ? "Modifica iscrizione" : "Nuova iscrizione"}</DialogTitle></DialogHeader>
          {editing !== null && <SignupForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
